import { DatePicker, Input, Divider } from "antd";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import ModalWrapper from "../../../../components/modal/modal";

const DEFAULT_NOTIFICATION_OFFSET_MINUTES = 8 * 60;

// function minutesToParts(totalMinutes = 0) {
//   const numeric = Number(totalMinutes || 0);
//   const sign = numeric < 0 ? "-" : "+";
//   const abs = Math.abs(numeric);

//   return {
//     sign,
//     hours: Math.floor(abs / 60),
//     minutes: abs % 60,
//   };
// }

// function partsToMinutes(sign, hours, minutes) {
//   const safeHours = Math.max(0, Math.min(23, Number(hours || 0)));
//   const safeMinutes = Math.max(0, Math.min(59, Number(minutes || 0)));
//   const total = safeHours * 60 + safeMinutes;

//   return sign === "-" ? -total : total;
// }

const sortByTitle = (a, b) =>
  String(a.title || "").localeCompare(String(b.title || ""), "en", {
    sensitivity: "base",
  });

const NotificationOffsetRow = ({ item, value, onChange, readOnly }) => {
  const [hours, setHours] = useState(Math.floor(Number(value || 0) / 60));
  const [minutes, setMinutes] = useState(Number(value || 0) % 60);

  useEffect(() => {
    const numeric = Number(value || 0);
    setHours(Math.floor(numeric / 60));
    setMinutes(numeric % 60);
  }, [value]);

  const apply = (nextHours, nextMinutes) => {
    const safeHours = Math.max(0, Math.min(23, Number(nextHours || 0)));
    const safeMinutes = Math.max(0, Math.min(59, Number(nextMinutes || 0)));
    onChange(item.id, safeHours * 60 + safeMinutes);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 12,
      }}
    >
      <div style={{ width: 260, fontSize: 14 }}>{item.title}</div>

      <Input
        value={hours}
        disabled={readOnly}
        style={{ width: 72 }}
        suffix="h"
        onChange={(e) => {
          const raw = e.target.value.replace(/[^\d]/g, "");
          setHours(Math.min(23, Number(raw || 0)));
        }}
        onBlur={() => apply(hours, minutes)}
      />

      <Input
        value={minutes}
        disabled={readOnly}
        style={{ width: 80 }}
        suffix="m"
        onChange={(e) => {
          const raw = e.target.value.replace(/[^\d]/g, "");
          setMinutes(Math.min(59, Number(raw || 0)));
        }}
        onBlur={() => apply(hours, minutes)}
      />
    </div>
  );
};

const normalizeScheduleDate = (value, fallbackDate) => {
  if (typeof value === "number" || (typeof value === "string" && /^-?\d+$/.test(value))) {
    const base = dayjs(fallbackDate);
    return base.isValid() ? base.add(Number(value), "day").format("YYYY-MM-DD") : null;
  }

  const parsed = dayjs(value || fallbackDate || undefined);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const ScheduleDateRow = ({ item, value, onChange, readOnly }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 12,
      }}
    >
      <div style={{ width: 260, fontSize: 14 }}>{item.title}</div>

      <DatePicker
        value={value ? dayjs(value) : null}
        disabled={readOnly}
        style={{ width: 160 }}
        onChange={(date) => onChange(item.id, date ? date.format("YYYY-MM-DD") : null)}
      />
    </div>
  );
};

const PlanDefaultDataModal = ({
  open,
  onCancel,
  onSave,
  scheduleFields = [],
  notificationFields = [],
  initialValues = { scheduleDefaults: {}, notificationDefaults: {} },
  defaultScheduleDate = dayjs().format("YYYY-MM-DD"),
  readOnly = false,
}) => {
  const [scheduleDefaults, setScheduleDefaults] = useState({});
  const [notificationDefaults, setNotificationDefaults] = useState({});

  useEffect(() => {
    if (!open) return;

    const nextScheduleDefaults = {};
    const nextNotificationDefaults = {};

    for (const item of scheduleFields) {
      nextScheduleDefaults[item.id] =
        normalizeScheduleDate(initialValues?.scheduleDefaults?.[item.id], defaultScheduleDate);
    }

    for (const item of notificationFields) {
      nextNotificationDefaults[item.id] =
        initialValues?.notificationDefaults?.[item.id] ?? DEFAULT_NOTIFICATION_OFFSET_MINUTES;
    }

    setScheduleDefaults(nextScheduleDefaults);
    setNotificationDefaults(nextNotificationDefaults);
  }, [open, initialValues, scheduleFields, notificationFields, defaultScheduleDate]);

  const sortedScheduleFields = useMemo(
    () => [...scheduleFields].sort(sortByTitle),
    [scheduleFields]
  );

  const sortedNotificationFields = useMemo(
    () => [...notificationFields].sort(sortByTitle),
    [notificationFields]
  );

  const updateScheduleDefault = (fieldId, value) => {
    setScheduleDefaults((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const updateNotificationDefault = (fieldId, value) => {
    setNotificationDefaults((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleOk = () => {
    onSave?.({
      scheduleDefaults,
      notificationDefaults,
    });
  };

  return (
    <ModalWrapper
      isOpen={open}
      title="Set default data"
      onOk={readOnly ? onCancel : handleOk}
      onCancel={onCancel}
      okText={readOnly ? "Close" : "Save"}
      cancelButtonProps={{
        size: "large",
        style: { display: readOnly ? "none" : "" },
      }}
      okButtonProps={{
        size: "large",
      }}
      width={760}
    >
      <Divider style={{ margin: "20px 0" }}/>
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontWeight: 600, marginBottom: 12 }}>Schedule defaults</p>

        {sortedScheduleFields.length === 0 ? (
          <p style={{ margin: 0, color: "var(--text-2)" }}>
            No schedule triggers used in this plan
          </p>
        ) : (
          sortedScheduleFields.map((item) => (
            <ScheduleDateRow
              key={item.id}
              item={item}
              value={scheduleDefaults[item.id] ?? normalizeScheduleDate(null, defaultScheduleDate)}
              onChange={updateScheduleDefault}
              readOnly={readOnly}
            />
          ))
        )}
      </div>

      <div>
        <p style={{ fontWeight: 600, marginBottom: 12 }}>
          Notification defaults
        </p>

        {sortedNotificationFields.length === 0 ? (
          <p style={{ margin: 0, color: "var(--text-2)" }}>
            No relative notification triggers used in this plan
          </p>
        ) : (
          sortedNotificationFields.map((item) => (
            <NotificationOffsetRow
              key={item.id}
              item={item}
              value={notificationDefaults[item.id] ?? DEFAULT_NOTIFICATION_OFFSET_MINUTES}
              onChange={updateNotificationDefault}
              readOnly={readOnly}
            />
          ))
        )}
      </div>
      <Divider style={{ margin: "20px 0" }} />
    </ModalWrapper>
  );
};

export default PlanDefaultDataModal;
